/**
 * Risk Analysis Data Models
 * Based on the Risk Analysis DTOs from Spring Boot backend
 */

// Risk Score DTO
export class RiskScoreDTO {
  constructor(data = {}) {
    this.category = data.category || '';
    this.score = data.score || 0; // 0-100
    this.description = data.description || '';
    this.impact = data.impact || '';
    this.likelihood = data.likelihood || '';
    this.factors = data.factors || [];
  }

  toJSON() {
    return {
      category: this.category,
      score: this.score,
      description: this.description,
      impact: this.impact,
      likelihood: this.likelihood,
      factors: this.factors
    };
  }

  getScoreColor() {
    if (this.score >= 80) return { text: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' };
    if (this.score >= 60) return { text: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' };
    if (this.score >= 40) return { text: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200' };
    return { text: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' };
  }

  getImpactColor() {
    switch (this.impact.toLowerCase()) {
      case 'high': return { text: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300' };
      case 'medium': return { text: 'text-yellow-700', bg: 'bg-yellow-100', border: 'border-yellow-300' };
      case 'low': return { text: 'text-green-700', bg: 'bg-green-100', border: 'border-green-300' };
      default: return { text: 'text-gray-700', bg: 'bg-gray-100', border: 'border-gray-300' };
    }
  }

  getLikelihoodColor() {
    switch (this.likelihood.toLowerCase()) {
      case 'high': return { text: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300' };
      case 'medium': return { text: 'text-yellow-700', bg: 'bg-yellow-100', border: 'border-yellow-300' };
      case 'low': return { text: 'text-green-700', bg: 'bg-green-100', border: 'border-green-300' };
      default: return { text: 'text-gray-700', bg: 'bg-gray-100', border: 'border-gray-300' };
    }
  }

  validate() {
    const errors = [];
    
    if (!this.category) {
      errors.push('Risk category is required');
    }
    
    if (this.score < 0 || this.score > 100) {
      errors.push('Risk score must be between 0 and 100');
    }
    
    if (!['high', 'medium', 'low'].includes(this.impact.toLowerCase())) {
      errors.push('Impact must be high, medium, or low');
    }
    
    if (!['high', 'medium', 'low'].includes(this.likelihood.toLowerCase())) {
      errors.push('Likelihood must be high, medium, or low');
    }
    
    return errors;
  }
}

// Risk Analysis DTO
export class RiskAnalysisDTO {
  constructor(data = {}) {
    // Basic Information
    this.analysisId = data.analysisId || data.analysis_id || '';
    this.companyName = data.companyName || data.company_name || '';
    this.ticker = data.ticker || '';
    this.industry = data.industry || '';
    this.analysisDate = data.analysisDate || data.analysis_date || new Date().toISOString();
    
    // Risk Scores (0-100)
    this.overallRiskScore = data.overallRiskScore || data.overall_risk_score || 0;
    this.supplyChainRiskScore = data.supplyChainRiskScore || data.supply_chain_risk_score || 0;
    this.industryRiskScore = data.industryRiskScore || data.industry_risk_score || 0;
    this.newsSentimentScore = data.newsSentimentScore || data.news_sentiment_score || 0;
    
    // Individual Risk Scores
    this.individualRiskScores = (data.individualRiskScores || data.individual_risk_scores || []).map(score => new RiskScoreDTO(score));
    
    // Analysis Documentation
    this.analysisSummary = data.analysisSummary || data.analysis_summary || '';
    this.keyFindings = data.keyFindings || data.key_findings || [];
    this.riskTrends = data.riskTrends || data.risk_trends || [];
    this.recommendations = data.recommendations || [];
    
    // News Analysis
    this.analyzedNewsCount = data.analyzedNewsCount || data.analyzed_news_count || 0;
    this.positiveNewsCount = data.positiveNewsCount || data.positive_news_count || 0;
    this.negativeNewsCount = data.negativeNewsCount || data.negative_news_count || 0;
    this.neutralNewsCount = data.neutralNewsCount || data.neutral_news_count || 0;
    this.analyzedNewsIds = data.analyzedNewsIds || data.analyzed_news_ids || [];
    
    // Confidence and Reliability
    this.analysisConfidence = data.analysisConfidence || data.analysis_confidence || 0;
    this.dataQualityScore = data.dataQualityScore || data.data_quality_score || 0;
  }

  toJSON() {
    return {
      analysisId: this.analysisId,
      companyName: this.companyName,
      ticker: this.ticker,
      industry: this.industry,
      analysisDate: this.analysisDate,
      overallRiskScore: this.overallRiskScore,
      supplyChainRiskScore: this.supplyChainRiskScore,
      industryRiskScore: this.industryRiskScore,
      newsSentimentScore: this.newsSentimentScore,
      individualRiskScores: this.individualRiskScores.map(score => score.toJSON()),
      analysisSummary: this.analysisSummary,
      keyFindings: this.keyFindings,
      riskTrends: this.riskTrends,
      recommendations: this.recommendations,
      analyzedNewsCount: this.analyzedNewsCount,
      positiveNewsCount: this.positiveNewsCount,
      negativeNewsCount: this.negativeNewsCount,
      neutralNewsCount: this.neutralNewsCount,
      analyzedNewsIds: this.analyzedNewsIds,
      analysisConfidence: this.analysisConfidence,
      dataQualityScore: this.dataQualityScore
    };
  }

  getOverallRiskLevel() {
    if (this.overallRiskScore >= 80) return 'Critical';
    if (this.overallRiskScore >= 60) return 'High';
    if (this.overallRiskScore >= 40) return 'Medium';
    if (this.overallRiskScore >= 20) return 'Low';
    return 'Very Low';
  }

  getOverallRiskColor() {
    if (this.overallRiskScore >= 80) return { text: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' };
    if (this.overallRiskScore >= 60) return { text: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' };
    if (this.overallRiskScore >= 40) return { text: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200' };
    if (this.overallRiskScore >= 20) return { text: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' };
    return { text: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' };
  }

  getNewsSentimentColor() {
    const total = this.positiveNewsCount + this.negativeNewsCount + this.neutralNewsCount;
    if (total === 0) return { text: 'text-gray-600', bg: 'bg-gray-50' };
    
    const sentiment = (this.positiveNewsCount - this.negativeNewsCount) / total;
    if (sentiment > 0.1) return { text: 'text-green-600', bg: 'bg-green-50' };
    if (sentiment < -0.1) return { text: 'text-red-600', bg: 'bg-red-50' };
    return { text: 'text-gray-600', bg: 'bg-gray-50' };
  }

  getConfidenceColor() {
    if (this.analysisConfidence >= 0.8) return 'text-green-600';
    if (this.analysisConfidence >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  }

  getDataQualityColor() {
    if (this.dataQualityScore >= 0.8) return 'text-green-600';
    if (this.dataQualityScore >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  }

  validate() {
    const errors = [];
    
    if (!this.companyName) {
      errors.push('Company name is required');
    }
    
    if (!this.ticker) {
      errors.push('Ticker is required');
    }
    
    if (!this.industry) {
      errors.push('Industry is required');
    }
    
    if (this.overallRiskScore < 0 || this.overallRiskScore > 100) {
      errors.push('Overall risk score must be between 0 and 100');
    }
    
    if (this.analysisConfidence < 0 || this.analysisConfidence > 1) {
      errors.push('Analysis confidence must be between 0.0 and 1.0');
    }
    
    if (this.dataQualityScore < 0 || this.dataQualityScore > 1) {
      errors.push('Data quality score must be between 0.0 and 1.0');
    }
    
    return errors;
  }
}

// Supply Chain Risk DTO
export class SupplyChainRiskDTO {
  constructor(data = {}) {
    this.name = data.name || '';
    this.reason = data.reason || '';
    this.impactLevel = data.impactLevel || data.impact_level || 'Medium';
    this.category = data.category || '';
    this.mitigationStrategies = data.mitigationStrategies || data.mitigation_strategies || [];
  }

  toJSON() {
    return {
      name: this.name,
      reason: this.reason,
      impactLevel: this.impactLevel,
      category: this.category,
      mitigationStrategies: this.mitigationStrategies
    };
  }

  getImpactLevelColor() {
    switch (this.impactLevel.toLowerCase()) {
      case 'high': return { text: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300' };
      case 'medium': return { text: 'text-yellow-700', bg: 'bg-yellow-100', border: 'border-yellow-300' };
      case 'low': return { text: 'text-green-700', bg: 'bg-green-100', border: 'border-green-300' };
      default: return { text: 'text-gray-700', bg: 'bg-gray-100', border: 'border-gray-300' };
    }
  }

  validate() {
    const errors = [];
    
    if (!this.name || this.name.length > 255) {
      errors.push('Risk name is required and must not exceed 255 characters');
    }
    
    if (!this.reason || this.reason.length > 2000) {
      errors.push('Risk reason is required and must not exceed 2000 characters');
    }
    
    if (!['high', 'medium', 'low'].includes(this.impactLevel.toLowerCase())) {
      errors.push('Impact level must be High, Medium, or Low');
    }
    
    if (!this.category || this.category.length > 100) {
      errors.push('Risk category is required and must not exceed 100 characters');
    }
    
    return errors;
  }
}

// Company Risk Summary DTO
export class CompanyRiskSummaryDTO {
  constructor(data = {}) {
    this.companyName = data.companyName || data.company_name || '';
    this.ticker = data.ticker || '';
    this.overallRiskScore = data.overallRiskScore || data.overall_risk_score || 0;
    this.riskLevel = data.riskLevel || data.risk_level || '';
    this.topRiskCategories = data.topRiskCategories || data.top_risk_categories || [];
    this.lastAnalysisDate = data.lastAnalysisDate || data.last_analysis_date || new Date().toISOString();
    this.analysisConfidence = data.analysisConfidence || data.analysis_confidence || 0;
  }

  toJSON() {
    return {
      companyName: this.companyName,
      ticker: this.ticker,
      overallRiskScore: this.overallRiskScore,
      riskLevel: this.riskLevel,
      topRiskCategories: this.topRiskCategories,
      lastAnalysisDate: this.lastAnalysisDate,
      analysisConfidence: this.analysisConfidence
    };
  }

  getRiskLevelColor() {
    switch (this.riskLevel.toLowerCase()) {
      case 'critical': return { text: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' };
      case 'high': return { text: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' };
      case 'medium': return { text: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200' };
      case 'low': return { text: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' };
      case 'very low': return { text: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' };
      default: return { text: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200' };
    }
  }

  getConfidenceColor() {
    if (this.analysisConfidence >= 0.8) return 'text-green-600';
    if (this.analysisConfidence >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  }

  validate() {
    const errors = [];
    
    if (!this.companyName) {
      errors.push('Company name is required');
    }
    
    if (!this.ticker) {
      errors.push('Ticker is required');
    }
    
    if (this.overallRiskScore < 0 || this.overallRiskScore > 100) {
      errors.push('Overall risk score must be between 0 and 100');
    }
    
    if (this.analysisConfidence < 0 || this.analysisConfidence > 1) {
      errors.push('Analysis confidence must be between 0.0 and 1.0');
    }
    
    return errors;
  }
}

// Risk Level Enum
export const RISK_LEVELS = {
  VERY_LOW: 'very low',
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

// Risk Category Enum
export const RISK_CATEGORIES = {
  SUPPLY_CHAIN: 'supply_chain',
  FINANCIAL: 'financial',
  OPERATIONAL: 'operational',
  REGULATORY: 'regulatory',
  MARKET: 'market',
  TECHNOLOGY: 'technology',
  ENVIRONMENTAL: 'environmental',
  GEOPOLITICAL: 'geopolitical'
};

// Impact Level Enum
export const IMPACT_LEVELS = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low'
};

// Likelihood Enum
export const LIKELIHOOD_LEVELS = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low'
};

// Model Factory Functions
export const createRiskScore = (data) => new RiskScoreDTO(data);
export const createRiskAnalysis = (data) => new RiskAnalysisDTO(data);
export const createSupplyChainRisk = (data) => new SupplyChainRiskDTO(data);
export const createCompanyRiskSummary = (data) => new CompanyRiskSummaryDTO(data);

// Utility Functions
export const getRiskScoreColor = (score) => {
  if (score >= 80) return { text: 'text-red-600', bg: 'bg-red-50' };
  if (score >= 60) return { text: 'text-orange-600', bg: 'bg-orange-50' };
  if (score >= 40) return { text: 'text-yellow-600', bg: 'bg-yellow-50' };
  if (score >= 20) return { text: 'text-blue-600', bg: 'bg-blue-50' };
  return { text: 'text-green-600', bg: 'bg-green-50' };
};

export const getRiskLevelFromScore = (score) => {
  if (score >= 80) return RISK_LEVELS.CRITICAL;
  if (score >= 60) return RISK_LEVELS.HIGH;
  if (score >= 40) return RISK_LEVELS.MEDIUM;
  if (score >= 20) return RISK_LEVELS.LOW;
  return RISK_LEVELS.VERY_LOW;
};

export const formatRiskScore = (score) => {
  return `${score.toFixed(1)}/100`;
};

export const formatConfidenceScore = (confidence) => {
  return `${(confidence * 100).toFixed(1)}%`;
};
